import { useCallback, useEffect, useRef, useState } from 'react';
import { SessionClient } from './SessionClient';
import { TabletopAction, TabletopEvent } from '@/state/tabletopActions';
import { ServerMessage, PlayerSummary, ChatHistoryEntry } from '@/state/multiplayerTypes';
import { TabletopState } from '@/components/tabletop/types';
import { getStoredPlayerId, setStoredPlayerId } from './sessionIdentityStorage';

type SessionStatus = 'idle' | 'connecting' | 'connected' | 'reconnecting';

interface RemoteStateMeta {
  events?: TabletopEvent[];
  initiatedBy?: string | null;
}

interface UseMultiplayerSessionOptions {
  onState?: (state: TabletopState, meta: RemoteStateMeta) => void;
  onError?: (message: string) => void;
}

interface UseMultiplayerSessionResult {
  status: SessionStatus;
  sessionId: string | null;
  playerId: string | null;
  players: PlayerSummary[];
  version: number;
  lastError: string | null;
  chatMessages: ChatHistoryEntry[];
  connect: (sessionId: string, playerName?: string) => void;
  disconnect: () => void;
  sendAction: (action: TabletopAction) => void;
  sendChat: (text: string) => void;
  requestSync: () => void;
}

export function useMultiplayerSession({
  onState,
  onError
}: UseMultiplayerSessionOptions = {}): UseMultiplayerSessionResult {
  const clientRef = useRef<SessionClient | null>(null);
  const pendingSessionIdRef = useRef<string | null>(null);
  const manualDisconnectRef = useRef(false);
  const [status, setStatus] = useState<SessionStatus>('idle');
  const [sessionId, setSessionId] = useState<string | null>(null);
  const [playerId, setPlayerId] = useState<string | null>(null);
  const playerIdRef = useRef<string | null>(null);
  const [players, setPlayers] = useState<PlayerSummary[]>([]);
  const [version, setVersion] = useState(0);
  const [lastError, setLastError] = useState<string | null>(null);
  const [chatMessages, setChatMessages] = useState<ChatHistoryEntry[]>([]);

  const handleMessage = useCallback(
    (message: ServerMessage) => {
      switch (message.type) {
        case 'init': {
          setStatus('connected');
          setVersion(message.version);
          setPlayers(message.players);
          setPlayerId(message.playerId);
          setSessionId(pendingSessionIdRef.current);
          setLastError(null);
          if (pendingSessionIdRef.current) {
            setStoredPlayerId(pendingSessionIdRef.current, message.playerId);
          }
          onState?.(message.state, { initiatedBy: null });
          break;
        }
        case 'patch': {
          setVersion(message.version);
          onState?.(message.state, { events: message.events, initiatedBy: message.initiatedBy });
          break;
        }
        case 'presence': {
          setPlayers(message.players);
          break;
        }
        case 'error': {
          setLastError(message.message);
          onError?.(message.message);
          break;
        }
        case 'pong': {
          // no-op
          break;
        }
        case 'chat': {
          setChatMessages(prev => [message.entry, ...prev]);
          break;
        }
        case 'chat-history': {
          setChatMessages(message.entries);
          break;
        }
        default:
          break;
      }
    },
    [onState, onError]
  );

  const handleClose = useCallback(
    () => {
      if (manualDisconnectRef.current) {
        manualDisconnectRef.current = false;
        setStatus('idle');
        setSessionId(null);
        setPlayers([]);
        setVersion(0);
        setPlayerId(null);
        return;
      }
      setStatus('reconnecting');
    },
    []
  );

  const handleError = useCallback(
    (event: Event) => {
      const message = 'Connection error. Retrying...';
      setLastError(message);
      onError?.(message);
      console.error('Session socket error', event);
    },
    [onError]
  );

  const connect = useCallback(
    (targetSessionId: string, playerName?: string) => {
      if (typeof window === 'undefined') {
        return;
      }

      pendingSessionIdRef.current = targetSessionId;

      if (clientRef.current) {
        clientRef.current.disconnect();
      }

      const storedPlayerId = getStoredPlayerId(targetSessionId);
      playerIdRef.current = storedPlayerId ?? null;

      const client = new SessionClient({
        sessionId: targetSessionId,
        playerId: playerIdRef.current ?? undefined,
        playerName,
        onMessage: handleMessage,
        onClose: handleClose,
        onError: handleError
      });

      clientRef.current = client;
      setStatus('connecting');
      setLastError(null);
      client.connect();
    },
    [handleMessage, handleClose, handleError]
  );

  const disconnect = useCallback(() => {
    manualDisconnectRef.current = true;
    clientRef.current?.disconnect();
    clientRef.current = null;
    pendingSessionIdRef.current = null;

    // Immediately update state instead of waiting for close event
    setStatus('idle');
    setSessionId(null);
    setPlayers([]);
    setVersion(0);
    setPlayerId(null);
    setLastError(null);
    setChatMessages([]);
  }, []);

  const sendAction = useCallback((action: TabletopAction) => {
    clientRef.current?.send({ type: 'action', action });
  }, []);

  const sendChat = useCallback((text: string) => {
    clientRef.current?.send({ type: 'chat', text });
  }, []);

  const requestSync = useCallback(() => {
    clientRef.current?.send({ type: 'sync-request' });
  }, []);

  useEffect(() => {
    playerIdRef.current = playerId;
  }, [playerId]);

  useEffect(() => {
    return () => {
      clientRef.current?.disconnect();
      clientRef.current = null;
    };
  }, []);

  return {
    status,
    sessionId,
    playerId,
    players,
    version,
    lastError,
    chatMessages,
    connect,
    disconnect,
    sendAction,
    sendChat,
    requestSync
  };
}
